import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { FaEdit, FaEye, FaSyncAlt, FaTrashAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import styled from 'styled-components';
import {
  deleteTiposBoletinAction,
  getAllTiposBoletinAction,
  setActivoTiposBoletinAction,
} from '../../../../store/actions';
import { IS_LIST_TIPOS_BOLETIN } from '../../../../store/types';

import {
  buildTableStyles,
  ListContainer,
  paginationComponentOptions,
  TableArea,
  useTableScrollHeight,
} from './listShared';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #212529;
  }

  span {
    font-size: 0.8rem;
    color: #6c757d;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NombreCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  strong {
    font-size: 0.88rem;
    color: #212529;
  }

  span {
    font-size: 0.72rem;
    color: #868e96;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
  flex-wrap: wrap;
`;

const customStyles = buildTableStyles('56px');

const ListadoTiposBoletin = ({ onEdit, onNew }) => {
  const dispatch = useDispatch();
  const tiposBoletinState = useSelector((state) => state.tiposBoletin);
  const { tableAreaRef, scrollHeight } = useTableScrollHeight();

  const [loading, setLoading] = useState(false);
  const [isModalDelete, setIsModalDelete] = useState(false);
  const [dataDelete, setDataDelete] = useState(null);

  const fetchTipos = useCallback(async () => {
    setLoading(true);
    await getAllTiposBoletinAction(dispatch, null);
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    fetchTipos();
  }, [fetchTipos]);

  const handleView = useCallback(
    (row, e) => {
      e?.stopPropagation?.();
      setActivoTiposBoletinAction(dispatch, row);
      dispatch({ type: IS_LIST_TIPOS_BOLETIN, payload: false });
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (row, e) => {
      e?.stopPropagation?.();
      onEdit?.(row);
    },
    [onEdit],
  );

  const handleDeleteClick = useCallback((row, e) => {
    e.stopPropagation();
    setDataDelete(row);
    setIsModalDelete(true);
  }, []);

  const toggleDelete = () => {
    setIsModalDelete(false);
    setDataDelete(null);
  };

  const columns = useMemo(
    () => [
      {
        name: 'ID',
        width: '72px',
        selector: (row) => row.id,
        sortable: true,
        center: true,
      },
      {
        name: 'Nombre',
        grow: 2,
        minWidth: '220px',
        selector: (row) => row.nombre,
        sortable: true,
        cell: (row) => (
          <NombreCell>
            <strong>{row.nombre}</strong>
            <span>Tipo de boletín #{row.id}</span>
          </NombreCell>
        ),
      },
      {
        name: 'Estado',
        width: '120px',
        selector: (row) => row.isActivo,
        sortable: true,
        center: true,
        cell: (row) => (
          <Badge
            color={row.isActivo ? 'success' : 'danger'}
            pill
            style={{ fontSize: '0.72rem' }}
          >
            {row.isActivo ? 'Activo' : 'Bloqueado'}
          </Badge>
        ),
      },
      {
        name: 'Acciones',
        width: '200px',
        center: true,
        cell: (row) => (
          <ActionButtons>
            <Button
              size="sm"
              color="dark"
              outline
              title="Ver bloques"
              onClick={(e) => handleView(row, e)}
            >
              <FaEye />
            </Button>
            <Button
              size="sm"
              color="primary"
              outline
              title="Editar"
              onClick={(e) => handleEdit(row, e)}
            >
              <FaEdit />
            </Button>
            <Button
              size="sm"
              color="danger"
              outline
              title="Eliminar"
              onClick={(e) => handleDeleteClick(row, e)}
            >
              <FaTrashAlt />
            </Button>
          </ActionButtons>
        ),
      },
    ],
    [handleView, handleEdit, handleDeleteClick],
  );

  const total = tiposBoletinState.all.length;
  const activos = tiposBoletinState.all.filter((t) => t.isActivo).length;

  return (
    <ListContainer>
      <PageHeader>
        <HeaderInfo>
          <h5>Tipos de boletín</h5>
          <span>
            {total} tipo{total !== 1 ? 's' : ''} · {activos} activo
            {activos !== 1 ? 's' : ''}
          </span>
        </HeaderInfo>
        <HeaderActions>
          <Button
            color="light"
            size="sm"
            disabled={loading}
            onClick={fetchTipos}
          >
            {loading ? (
              <>
                <Spinner size="sm" /> Actualizando…
              </>
            ) : (
              <>
                <FaSyncAlt style={{ marginRight: 6 }} />
                Actualizar
              </>
            )}
          </Button>
          <Button color="success" size="sm" onClick={() => onNew?.()}>
            + Nuevo tipo
          </Button>
        </HeaderActions>
      </PageHeader>

      <TableArea ref={tableAreaRef}>
        <DataTable
          columns={columns}
          data={tiposBoletinState.all}
          pagination
          paginationComponentOptions={paginationComponentOptions}
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50]}
          dense
          striped
          pointerOnHover
          highlightOnHover
          customStyles={customStyles}
          fixedHeader
          fixedHeaderScrollHeight={scrollHeight}
          responsive
          progressPending={loading}
          noDataComponent={
            <div
              style={{ padding: '2.5rem', color: '#6c757d', textAlign: 'center' }}
            >
              <p style={{ margin: '0 0 1rem' }}>
                No hay tipos de boletín registrados.
              </p>
              <Button color="success" size="sm" onClick={() => onNew?.()}>
                Crear primer tipo
              </Button>
            </div>
          }
          onRowClicked={(row, event) => {
            if (event.target.closest('button')) return;
            handleView(row);
          }}
        />
      </TableArea>

      <Modal isOpen={isModalDelete} toggle={toggleDelete} centered>
        <ModalHeader toggle={toggleDelete}>Eliminar tipo</ModalHeader>
        <ModalBody>
          ¿Seguro que deseas eliminar <strong>{dataDelete?.nombre}</strong>? Se
          eliminarán también sus bloques asociados.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={toggleDelete}>
            Cancelar
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={async () => {
              await deleteTiposBoletinAction(dispatch, dataDelete.id);
              toggleDelete();
            }}
          >
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </ListContainer>
  );
};

export default ListadoTiposBoletin;
